<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { journalsApi } from '../../api/journalsApi'
import { logJournalHttpError } from '../../composables/useErrorHandler'
import { formatDateForInput, formatDateOnlyForAPI, parseDateFromInput } from '../../utils/dateUtils'

interface Props {
  modelValue: boolean
  journalId: number
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const dateFrom = ref<string>('')
const dateTo = ref<string>('')
const dateFromMenu = ref(false)
const dateToMenu = ref(false)
const isLoading = ref(false)
const errorMessage = ref<string>('')

const exportOptions = [
  { label: 'Все записи', value: 'all' as const, icon: 'ri-file-list-line' },
  { label: 'По месяцам', value: 'monthly' as const, icon: 'ri-calendar-line' },
  { label: 'По годам', value: 'yearly' as const, icon: 'ri-calendar-2-line' },
]

const isDateRangeValid = computed(() => {
  if (!dateFrom.value || !dateTo.value) {
    return true
  }
  const from = parseDateFromInput(dateFrom.value)
  const to = parseDateFromInput(dateTo.value)
  if (!from || !to) {
    return true
  }

  return from <= to
})

watch([dateFrom, dateTo], () => {
  errorMessage.value = ''
})

const onDateFromSelect = (date: string | Date | null) => {
  if (date) {
    dateFrom.value = formatDateForInput(new Date(date))
  }
  dateFromMenu.value = false
}

const onDateToSelect = (date: string | Date | null) => {
  if (date) {
    dateTo.value = formatDateForInput(new Date(date))
  }
  dateToMenu.value = false
}

const handleClose = () => {
  emit('update:modelValue', false)
  setTimeout(() => {
    dateFrom.value = ''
    dateTo.value = ''
    dateFromMenu.value = false
    dateToMenu.value = false
    errorMessage.value = ''
  }, 300)
}

const handleExport = async (groupBy: 'all' | 'monthly' | 'yearly') => {
  if (!isDateRangeValid.value) {
    errorMessage.value = 'Дата "от" не может быть позже даты "до"'

    return
  }

  isLoading.value = true
  errorMessage.value = ''

  const params: {
    group_by: 'all' | 'monthly' | 'yearly'
    created_after?: string
    created_before?: string
  } = {
    group_by: groupBy,
  }

  try {
    if (dateFrom.value) {
      const fromDate = parseDateFromInput(dateFrom.value)
      if (fromDate) {
        params.created_after = formatDateOnlyForAPI(fromDate)
      }
    }

    if (dateTo.value) {
      const toDate = parseDateFromInput(dateTo.value)
      if (toDate) {
        params.created_before = formatDateOnlyForAPI(toDate)
      }
    }

    await journalsApi.exportJournal(props.journalId, params)

    handleClose()
  } catch (error) {
    console.error('Ошибка при экспорте журнала:', error)
    logJournalHttpError(error, {
      function: 'exportJournal',
      store: 'JournalExportModal',
      payload: { journalId: props.journalId, groupBy, params },
    })
    errorMessage.value = 'Произошла ошибка при экспорте журнала. Попробуйте еще раз.'
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="500"
    @update:model-value="handleClose"
  >
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between">
        <span>Экспорт журнала</span>
        <VBtn
          icon
          variant="text"
          size="small"
          @click="handleClose"
        >
          <VIcon icon="ri-close-line" />
        </VBtn>
      </VCardTitle>

      <VCardText class="pa-4 journal-form-fields">
        <div class="mb-6">
          <h3 class="text-subtitle-1 mb-4">
            Период
          </h3>

          <VMenu
            v-model="dateFromMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            location="bottom start"
            min-width="auto"
          >
            <template #activator="{ props: menuProps }">
              <VTextField
                v-bind="menuProps"
                v-model="dateFrom"
                variant="outlined"
                persistent-placeholder
                class="mb-3 invisible-readonly"
                append-inner-icon="ri-calendar-line"
                hide-details
                readonly
                :disabled="isLoading"
                :error="!isDateRangeValid"
                @click="dateFromMenu = true"
                @click:append-inner.prevent.stop="dateFromMenu = true"
              >
                <template #label>
                  <span>Дата от</span>
                </template>
              </VTextField>
            </template>

            <VDatePicker
              :model-value="dateFrom"
              @update:model-value="onDateFromSelect"
            />
          </VMenu>

          <VMenu
            v-model="dateToMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            location="bottom start"
            min-width="auto"
          >
            <template #activator="{ props: menuProps }">
              <VTextField
                v-bind="menuProps"
                v-model="dateTo"
                variant="outlined"
                persistent-placeholder
                class="mb-4 invisible-readonly"
                append-inner-icon="ri-calendar-line"
                hide-details
                readonly
                :disabled="isLoading"
                :error="!isDateRangeValid"
                @click="dateToMenu = true"
                @click:append-inner.prevent.stop="dateToMenu = true"
              >
                <template #label>
                  <span>Дата до</span>
                </template>
              </VTextField>
            </template>

            <VDatePicker
              :model-value="dateTo"
              @update:model-value="onDateToSelect"
            />
          </VMenu>

          <VAlert
            v-if="!isDateRangeValid"
            type="error"
            density="compact"
            class="mb-3"
          >
            Дата "от" не может быть позже даты "до"
          </VAlert>

          <VAlert
            v-if="errorMessage"
            type="error"
            density="compact"
            class="mb-3"
            closable
            @click:close="errorMessage = ''"
          >
            {{ errorMessage }}
          </VAlert>
        </div>

        <div>
          <h3 class="text-subtitle-1 mb-4">
            Тип экспорта
          </h3>

          <VList class="export-options-list">
            <VListItem
              v-for="option in exportOptions"
              :key="option.value"
              :disabled="isLoading || !isDateRangeValid"
              class="export-option-item"
              @click="handleExport(option.value)"
            >
              <template #prepend>
                <VIcon
                  :icon="option.icon"
                  size="20"
                />
              </template>

              <VListItemTitle>{{ option.label }}</VListItemTitle>

              <template #append>
                <VIcon
                  v-if="isLoading"
                  icon="ri-loader-4-line"
                  class="rotating-icon"
                />
                <VIcon
                  v-else
                  icon="ri-download-2-line"
                />
              </template>
            </VListItem>
          </VList>
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
@use '@/modules/journals/styles/journalFormFields' as jf;

.journal-form-fields {
  @include jf.fields-height;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.rotating-icon {
  animation: rotate 1s linear infinite;
}

.export-options-list {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
}

.export-option-item {
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not([disabled]) {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }
}
</style>
